/**
 * Type + temperature grouping for the chef kitchen lists (Sheila,
 * 2026-08-22): the Recipes make-list and the Deliverables packing list
 * group rows by TYPE — Mains / Sides / Condiments — with
 * each type's rows ORDERED by the item's canonical temperature
 * (hot → chilled → ambient; no temperature headings — the ordering
 * itself is the organization, per Sheila's follow-up).
 *
 * Classification rules:
 *   - MAINS includes any deliverable that has a main in it (a bundle
 *     with a main entree groups as a main even when it also carries
 *     sides — `packageProductType` already projects that onto
 *     `Deliverable.productType`).
 *   - CONDIMENTS is a deliverable whose items are all condiments, or a
 *     condiment component.
 *   - SIDES is everything else — fruit/veg, grains/baked, desserts,
 *     add-ons AND beverages. Beverages deliberately do NOT get their
 *     own group: juice counts as both a side and a beverage, and a
 *     separate bucket made the split look arbitrary (Sheila's
 *     follow-up, 2026-08-22). MILK is the exception on the chef Orders
 *     screens: a milk package is its own card above the mealtimes
 *     (PROD-1502, `Campus.milk`), so it never reaches these groups there.
 *     Non-milk beverages (juice) and milk inside a mixed meal package
 *     still fold into Sides.
 *
 * Temperature is the row's CANONICAL temperature (the same
 * hot/chilled/ambient the shipment lines carry and the temp-log
 * checkpoints read) — never re-derived. Rows with no temperature on
 * file (e.g. forecast rows, weekly milk projections) sort last within
 * their type.
 */

import type { Component, Deliverable, Temperature } from '@/lib/chefs/types'

export type PackTypeGroup = 'mains' | 'sides' | 'condiments'

export const PACK_TYPE_ORDER: readonly PackTypeGroup[] = ['mains', 'sides', 'condiments']

export const PACK_TYPE_LABELS: Record<PackTypeGroup, string> = {
  mains: 'Mains',
  sides: 'Sides',
  condiments: 'Condiments',
}

/** Canonical sub-order within a type: hot, then chilled, then ambient. */
export const TEMPERATURE_GROUP_ORDER: readonly Temperature[] = ['hot', 'chilled', 'ambient']

/** Rank for sorting; unknown/absent temperature sorts last. */
export function temperatureGroupRank(temperature: Temperature | null | undefined): number {
  const index = TEMPERATURE_GROUP_ORDER.indexOf(temperature as Temperature)
  return index === -1 ? TEMPERATURE_GROUP_ORDER.length : index
}

/** The hotter of two readings — used when one recipe rides in deliverables
 *  of different temperatures (the prep bench cares about the hot hold). */
export function hotterTemperature(a: Temperature | null, b: Temperature | null): Temperature | null {
  if (a === null) {
    return b
  }
  if (b === null) {
    return a
  }
  return temperatureGroupRank(a) <= temperatureGroupRank(b) ? a : b
}

export function componentTypeGroup(c: Pick<Component, 'productType' | 'isCondiment'>): PackTypeGroup {
  if (c.isCondiment) {
    return 'condiments'
  }
  if (c.productType === 'main') {
    return 'mains'
  }
  // Beverages fold into Sides (juice is both) — see the header comment.
  return 'sides'
}

export function deliverableTypeGroup(d: Pick<Deliverable, 'productType' | 'components'>): PackTypeGroup {
  // A deliverable whose items are ALL condiments is a condiment pack —
  // checked first so a condiment-only bundle never reads as a side.
  if (d.components.length > 0 && d.components.every((c) => c.isCondiment)) {
    return 'condiments'
  }
  // "Includes any item that has a main in it": `packageProductType`
  // already resolves a bundle containing a main to productType 'main';
  // the component scan additionally catches shapes where the projection
  // led with another item.
  if (d.productType === 'main' || d.components.some((c) => !c.isCondiment && c.productType === 'main')) {
    return 'mains'
  }
  // Beverages fold into Sides (juice is both) — see the header comment.
  return 'sides'
}

export interface TypeGroup<T> {
  group: PackTypeGroup
  /** Every row in the type, temp-ordered (hot → chilled → ambient → unknown). */
  items: T[]
}

/**
 * Split rows into the canonical type groups, each ORDERED by temperature
 * (no per-temperature headings — the ordering is the organization).
 * Groups with no rows are omitted; within a temperature the caller's
 * input order is preserved (callers pre-sort by name).
 */
export function groupByTypeAndTemp<T>(
  items: readonly T[],
  getGroup: (item: T) => PackTypeGroup,
  getTemperature: (item: T) => Temperature | null,
): TypeGroup<T>[] {
  const out: TypeGroup<T>[] = []
  for (const group of PACK_TYPE_ORDER) {
    const inGroup = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => getGroup(item) === group)
      // Stable: equal temps keep the caller's input order.
      .sort(
        (a, b) =>
          temperatureGroupRank(getTemperature(a.item)) - temperatureGroupRank(getTemperature(b.item)) ||
          a.index - b.index,
      )
      .map(({ item }) => item)
    if (inGroup.length > 0) {
      out.push({ group, items: inGroup })
    }
  }
  return out
}
